import React, { useState } from 'react';
import { css } from '@emotion/react';
import { BirdiTheme } from '@birdi/theme';
import { subscription as subscriptionAPI, stroot } from '@birdi/js-sdk';
import { useMetaSelector } from '../meta/reducer';
import { format } from 'date-fns';
import CloseSVG from '@birdi/icons/close.svg';
import InfoSVG from '@birdi/icons/info.svg';

export const TrialBanner: React.FC = () => {
  const [isDismissed, setIsDismissed] = useState(false);
  const meta = useMetaSelector();

  // Get trial data from Redux state
  const trialEnd = meta.organisation?.planData?.subscription?.trial_end;
  const now = Math.floor(Date.now() / 1000);

  // Only show banner if:
  // 1. Trial end exists
  // 2. Trial hasn't ended yet
  // 3. User hasn't dismissed it
  if (!trialEnd || trialEnd <= now || isDismissed) {
    return null;
  }

  // Calculate days left
  const secondsLeft = trialEnd - now;
  const daysLeft = Math.ceil(secondsLeft / (60 * 60 * 24));

  // Format the end date consistently with plan page using date-fns
  const endDate = format(trialEnd * 1000, 'dd MMM yyyy');

  const handleManageSubscription = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      const response = await subscriptionAPI.getStripePortal(
        stroot('trial-banner'),
      );
      if (response.body?.data) {
        window.location.href = response.body.data;
      }
    } catch (error) {
      console.error('Error accessing Stripe portal:', error);
    }
  };

  return (
    <div
      css={(theme: BirdiTheme) => css`
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${theme.lightOcean};
        padding: 0.875rem 1.5rem;
        box-sizing: border-box;
        width: 100%;
      `}
    >
      <div
        css={(theme: BirdiTheme) => css`
          display: flex;
          align-items: center;
          gap: 0.75rem;
        `}
      >
        <div
          css={(theme: BirdiTheme) => css`
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            svg {
              width: 20px;
              height: 20px;
              fill: none;
              stroke: ${theme.midnight};
              stroke-width: 2;
            }
          `}
        >
          <InfoSVG />
        </div>
        <div
          css={(theme: BirdiTheme) => css`
            color: ${theme.midnight};
            font-size: 1.1em;
            font-weight: 400;
            line-height: 1.5;
          `}
        >
        <strong>
          You're on a free trial — {daysLeft} {daysLeft === 1 ? 'day' : 'days'}{' '}
          left
        </strong>
        <br />
        Your trial ends on {endDate}. You'll be automatically charged on this
        date unless you cancel before then.{' '}
        <button
          onClick={handleManageSubscription}
          css={(theme: BirdiTheme) => css`
            background: none;
            border: none;
            color: ${theme.midnight};
            text-decoration: underline;
            cursor: pointer;
            padding: 0;
            font-size: inherit;
            font-weight: inherit;
            font-family: inherit;
            line-height: inherit;
            &:hover {
              opacity: 0.8;
            }
          `}
        >
          Manage Subscription
        </button>
        </div>
      </div>
      <button
        onClick={() => setIsDismissed(true)}
        aria-label="Dismiss banner"
        css={(theme: BirdiTheme) => css`
          position: absolute;
          right: 1.5rem;
          flex-shrink: 0;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: background 0.2s;
          &:hover {
            background: rgba(0, 0, 0, 0.05);
          }
        `}
      >
        <div
          css={(theme: BirdiTheme) => css`
            display: flex;
            align-items: center;
            justify-content: center;
            svg {
              width: 16px;
              height: 16px;
              fill: ${theme.midnight};
            }
          `}
        >
          <CloseSVG />
        </div>
      </button>
    </div>
  );
};
